import React, { useEffect, useState, useContext } from 'react';
import classnames from 'classnames';
import InputMask from 'react-input-mask';
import PropTypes from 'prop-types';
import { ClockIcon } from '@heroicons/react/20/solid';
import { LocaleContext } from 'providers/locale-settings-context';
import InputWithTrailingIcon from './forms/InputWithTrailingIcon';

const twentyFourHourClockMask = [/[0-2]/, /[0-9]/, ':', /[0-5]/, /[0-9]/];
const twelveHourClockMask = [
  /[0-1]/,
  /[0-9]/,
  ':',
  /[0-5]/,
  /[0-9]/,
  ' ',
  /(a|A|p|P)/,
];

export default function TimePicker({
  is24HourClock: is24HourClockProp,
  value,
  onChange,
  alwaysShowMask,
  disabled,
  className,
  dataTestId
}) {
  const localeContext = useContext(LocaleContext);
  const is24HourClockContext = localeContext?.localeData?.is24HourFormat;

  let is24HourClock;
  if (typeof is24HourClockProp !== 'undefined') {
    is24HourClock = is24HourClockProp;
  } else {
    is24HourClock = is24HourClockContext;
  }

  function getMask() {
    if (is24HourClock) {
      return [...twentyFourHourClockMask];
    }
    return [...twelveHourClockMask];
  }
  const [mask, setMask] = useState(getMask());

  useEffect(() => {
    // Conditional mask for the 2nd digit base on the first digit
    if (is24HourClock) {
      if ((value || '').startsWith('2')) {
        setMask([
          twentyFourHourClockMask[0],
          /[0-3]/,
          ':',
          twentyFourHourClockMask[3],
          twentyFourHourClockMask[4],
        ]);
      } else {
        setMask([...twentyFourHourClockMask]);
      }
    } else if ((value || '').startsWith('1')) {
      setMask([
        twelveHourClockMask[0],
        /[0-2]/,
        ...twelveHourClockMask.slice(2),
      ]);
    } else {
      setMask([...twelveHourClockMask]);
    }
  }, [value]);

  function beforeMaskedStateChange({ nextState }) {
    if (is24HourClock) {
      return nextState;
    }

    let value = nextState.value || '';

    if (value.match(/(a|A|p|P)/)) {
      value = value.replace(/(a|A|p|P)(m|M)?/, '$1M').toUpperCase();
      return { ...nextState, value };
    }
    return nextState;
  }

  function handleChange(e) {
    onChange(e);
  }

  return (
    (<InputMask
      mask={mask}
      value={value}
      data-testid={dataTestId}
      onChange={handleChange}
      alwaysShowMask={alwaysShowMask}
      beforeMaskedStateChange={beforeMaskedStateChange}
      disabled={disabled}
    >
      <InputWithTrailingIcon
        dataTestId="InputWithTrailingIcon-b6c5269f"
        className={className}
        passEventOnChange>
        <ClockIcon className="w-4 h-4" />
      </InputWithTrailingIcon>
    </InputMask>)
  );
}

TimePicker.props = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  alwaysShowMask: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.array,
  ]),
};

TimePicker.defaultProps = {
  alwaysShowMask: true,
  disabled: false,
};
